import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { ProblemDetails } from '../models/problem-details.model';
import { TokenService } from '../services/token.service';
import { TokenAtualizacaoService } from '../services/token-atualizacao.service';
import { Router } from '@angular/router';

export const ErrorInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
    const token = inject(TokenService);
    const tokenAtualizacaoService = inject(TokenAtualizacaoService);
    const messages = inject(MessageService);
    const router = inject(Router);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401) {
                const isRefreshUrl = req.url.includes('/autenticacao/token-atualizacao');
                const refreshAtivo = tokenAtualizacaoService.refreshEmProgresso();

                if (isRefreshUrl || refreshAtivo) {
                    return throwError(() => error);
                }
            }

            if (error.status === 0) {
                messages.add({
                    severity: 'error',
                    summary: 'Falha de conexão',
                    detail: 'Não foi possível se comunicar com o servidor.',
                });
                return throwError(() => error);
            }

            const problem = error.error as ProblemDetails;

            if (problem && (problem.title || problem.detail)) {
                const status = problem.status ?? error.status;

                switch (status) {
                    case 400: {
                        if (problem.errors && Object.keys(problem.errors).length > 0) {
                            const primeiroErro = Object.entries(problem.errors)[0];
                            const [, mensagens] = primeiroErro;
                            const primeiraMensagem = (mensagens ?? [])[0] ?? 'Erro de validação';

                            messages.add({ severity: 'warn', summary: 'Erro de validação', detail: primeiraMensagem, life: 8000 });
                        } else {
                            messages.add({ severity: 'warn', summary: 'Requisição inválida', detail: problem.detail ?? 'Verifique os dados enviados.' });
                        }
                        break;
                    }

                    case 401: {
                        const hasToken = token.buscarToken();

                        if (hasToken) {
                            token.limparSessao();
                            messages.add({ severity: 'warn', summary: 'Sessão expirada', detail: problem.detail ?? 'Sua sessão expirou. Por favor, faça login novamente.' });
                            router.navigate(['/autenticacao/login']);
                        } else {
                            messages.add({ severity: 'error', summary: 'Não autorizado', detail: problem.detail ?? 'Credenciais inválidas ou acesso negado.' });
                        }
                        break;
                    }

                    case 403: {
                        messages.add({ severity: 'error', summary: 'Acesso negado', detail: problem.detail ?? 'Você não tem permissão para realizar esta ação.' });
                        break;
                    }

                    case 404: {
                        messages.add({ severity: 'info', summary: 'Não encontrado', detail: problem.detail ?? 'O recurso solicitado não foi localizado.' });
                        break;
                    }

                    case 429: {
                        messages.add({ severity: 'warn', summary: 'Muitas tentativas', detail: 'Aguarde alguns instantes antes de tentar novamente.' });
                        break;
                    }

                    case 500: {
                        messages.add({ severity: 'error', summary: 'Erro interno do servidor', detail: problem.detail ?? 'Ocorreu um erro inesperado.', life: 10000 });
                        break;
                    }

                    default: {
                        messages.add({ severity: 'error', summary: 'Erro', detail: problem.detail ?? 'Algo deu errado.' });
                        break;
                    }
                }
            } else {
                messages.add({
                    severity: 'error',
                    summary: 'Erro inesperado',
                    detail: typeof error.error === 'string' ? error.error : 'Ocorreu um erro inesperado.',
                });
            }

            return throwError(() => error);
        })
    );
};
